<template>
  <div class="CoveragesForm">
    <h2 class="gl_form-title">
      {{ t(`copy_life_coverages-form_title-${answers.forWhom ?? 'me'}`) }}
    </h2>

    <p class="mb-4 text-black">
      {{ t(`copy_life_coverages-form_mandatory-coverages-title-${answers.forWhom}`) }}
    </p>

    <CoverageComplete
      v-for="coverage in mandatoryCoverages"
      :id="coverage.coverageCode"
      :is-mandatory="true"
      :checked="true"
      :key="coverage.coverageCode"
      :text="coverage.title"
      :tooltip="coverage.tooltip"
    />

    <v-divider class="my-4" />

    <p class="mb-4 text-black">
      {{ titleValue }}
    </p>

    <CoverageComplete
      v-for="(coverage, i) in optionalCoverages"
      class="mb-4"
      :id="coverage.coverageCode"
      :key="coverage.coverageCode"
      :text="coverage.title"
      :tooltip="coverage.tooltip"
      :capitals="getSliderCapitals(coverage)"
      v-model:checked="optionalToSave[i].checked"
      v-model:capital="optionalToSave[i].value"
    />
  </div>
</template>

<script lang="ts" setup>
import { onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLifeStore } from '@/modules/life/rater/stores/index'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'
import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
import { useCoverages } from '@/modules/life/common/composables/useCoveragesComposable'
import CoverageComplete from '@/modules/common/common/components/coverages/CoverageComplete.vue'
import type LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
import { ref } from 'vue'

const { t } = useI18n()
const loadingStore = useLoadingStore()
const sharedGtmDataStore = useSharedGtmDataStore()
const lifeStore = useLifeStore()
const errorManager = useErrorManagerComposable()
const {
  mandatoryCoverages,
  optionalCoverages,
  mandatoryToSave,
  optionalToSave,
  getSliderCapitals,
  loadCoverages
} = useCoverages()

defineExpose({ triggerSubmit })

interface PropsInterface {
  nextForm: String[]
  answers: Record<string, any>
  configurations: LifeConfigurations
}
const props = withDefaults(defineProps<PropsInterface>(), {})

const titleValue = ref('')

function triggerSubmit() {
  const page = props.nextForm[0]

  const insureds = props.answers.insureds
  const coverages = mandatoryToSave.value.concat(optionalToSave.value)
  insureds[0].additionalCoverages = coverages
  const answers = { insureds }
  sharedGtmDataStore.additionalCoverages = optionalToSave.value.some((coverage) => coverage.checked)
  return { page, answers }
}

async function beforeMount() {
  try {
    loadingStore.startLoading()
    titleValue.value =
      props.configurations.insuranceTypes.family.id === props.answers.insuranceType
        ? props.configurations.insuranceTypes.family.textOptionalCoverageSelectInfo
        : props.configurations.insuranceTypes.mortgage.textOptionalCoverageSelectInfo
    const insuranceType = props.answers.insuranceType
    const storeCoverages = await lifeStore.loadCoverages({ insuranceType, preventCache: true })
    const insured = props.answers.insureds[0]
    const coveragesCapital = props.answers.coveragesCapital
    await loadCoverages(
      storeCoverages,
      insured,
      props.configurations.capitalOptions,
      coveragesCapital
    )
  } catch (error) {
    errorManager.handleError(error)
  } finally {
    loadingStore.stopLoading()
  }
}

onBeforeMount(beforeMount)
</script>
